import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

// CONSTITUTIONAL MONETIZATION RULE (Message 264) —
// Feature bullets below must map to operational backend routes/UI.
// Two tier-classes: Consumer Intelligence vs Capital Intelligence.
const TIERS = [
  {
    key: "free",
    label: "Free",
    tier_class: "Consumer Intelligence",
    color: "#60B8E8",
    blurb: "Public ORACLE intelligence · LOW-severity signals",
    features: ["Public ORACLE 7-mode console", "LOW severity signals only", "Public chyron + compliance lock"],
  },
  {
    key: "professional",
    label: "Professional",
    tier_class: "Consumer Intelligence",
    color: "#7AC860",
    blurb: "Daily research desk loadout",
    features: [
      "Saved Watchlists",
      "MEDIUM/HIGH severity signals",
      "Standard Intelligence Dossier (PDF)",
      "ORACLE Predictive Command™ access",
      "Notification Fabric (Email/Slack) — channels labeled MOCKED_AWAITING_KEYS until configured",
    ],
  },
  {
    key: "executive",
    label: "Executive",
    tier_class: "Capital Intelligence",
    color: "#F5C842",
    blurb: "Executive briefing + EXTREME signal access",
    features: [
      "Everything in Professional",
      "EXTREME severity signals",
      "Executive Dossiers v3",
      "ORACLE Global Capital Command™ (16th domain)",
      "Funding Command Center™ access",
    ],
  },
  {
    key: "enterprise",
    label: "Enterprise",
    tier_class: "Capital Intelligence",
    color: "#E8A020",
    blurb: "AdminOne + Enterprise Audit Orchestration access",
    features: [
      "Everything in Executive",
      "AdminOne Command Center",
      "Signal Fabric Command (create/escalate)",
      "Enterprise Audit Orchestration + governance widgets",
      "Audit log + CSV/JSON export endpoints",
    ],
  },
];

export default function AdminMembership() {
  const [tiers, setTiers] = useState([]);
  const [defaultTier, setDefaultTier] = useState("professional");
  const [events, setEvents] = useState([]);
  const [channels, setChannels] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    adminApi.membershipTiers().then(({ data }) => {
      setTiers(data?.tiers || []);
      setDefaultTier(data?.default || "professional");
    }).catch(() => {});
    adminApi.notificationsEvents().then(({ data }) => {
      setEvents(data?.events || []);
      setChannels(data?.channels || {});
    }).catch(() => {});
  }, []);

  const test = async () => {
    setBusy(true); setToast("");
    try {
      const { data } = await adminApi.channelsTest({ event: "Membership Test Broadcast" });
      setToast(`Dispatched · ${JSON.stringify(data?.channels || {}).slice(0, 200)}`);
    } catch { setToast("Dispatch failed"); }
    finally { setBusy(false); setTimeout(() => setToast(""), 6000); }
  };

  return (
    <div className="admin-page mesh-shell" data-testid="admin-membership">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />

      <div className="admin-page__head">
        <h2 className="admin-page__title">Membership Intelligence</h2>
        <div className="mesh-eyebrow">Free · Professional · Executive · Enterprise</div>
      </div>

      <section className="admin-section mesh-section" data-testid="membership-tiers">
        <h3 className="admin-section__title">Tiers · default: {defaultTier}</h3>
        <div className="mesh-tier-grid">
          {TIERS.map((t) => (
            <article key={t.key} className="mesh-tier-card" style={{ borderColor: t.color }} data-testid={`tier-card-${t.key}`}>
              <header className="mesh-tier-card__head" style={{ color: t.color }}>
                <div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.75, marginBottom: 4 }}>
                  {t.tier_class}
                </div>
                {t.label}
              </header>
              <p className="mesh-tier-card__blurb">{t.blurb}</p>
              <ul className="mesh-tier-card__list">
                {t.features.map((f) => <li key={f}>{f}</li>)}
              </ul>
              {tiers.includes(t.key) && <div className="mesh-tier-card__foot">API tier key: <code>{t.key}</code></div>}
            </article>
          ))}
        </div>
      </section>

      <section className="admin-section mesh-section" data-testid="membership-notifications">
        <h3 className="admin-section__title">Notification Fabric</h3>
        <div className="mesh-channels">
          {Object.entries(channels).map(([k, v]) => (
            <span key={k} className={`mesh-pill ${v ? "mesh-pill--gold" : "mesh-pill--ghost"}`}>{k}: {v ? "ON" : "STANDBY"}</span>
          ))}
        </div>
        <div className="mesh-channels" style={{ marginTop: 8 }}>
          {events.map((e) => (
            <span key={e.key} className="mesh-pill mesh-pill--ghost" data-testid={`notification-event-${e.key}`}>{e.label}</span>
          ))}
        </div>
        <div style={{ marginTop: 10 }}>
          <button type="button" className="admin-linkbtn" disabled={busy} onClick={test} data-testid="membership-test-dispatch">
            {busy ? "Dispatching…" : "Send Test Broadcast"}
          </button>
          {toast && <span className="mesh-toast" style={{ marginLeft: 10 }}>{toast}</span>}
        </div>
        <div className="admin-foot-note" style={{ marginTop: 10 }}>
          Channels marked STANDBY are silent no-op until their environment variables are configured (SMTP_*, TWILIO_*, ORACLE_*_WEBHOOK_URL).
        </div>
      </section>
    </div>
  );
}
